import { formatScheduleLabel, useTimeFormat } from '../../lib/timeFormat'
import { buildCalendar, weeksOf, WEEKDAYS, type CalendarDay } from './calendarLayout'
import { COVERAGE_REPORT_STATUS_LABEL, type CoverageReportStatus } from './coverageReport'
import type { PayPeriodRow } from './payPeriodGridFigures'
import { todayIso } from './reportDates'

// Every day is the same size, so the names beneath its counts are capped.
const MAX_LINES = 4

const dayNumber = (iso: string) => String(Number(iso.split('-')[2]))
const longDate = (iso: string) => {
  const [year, month, day] = iso.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' })
}
const isWeekend = (iso: string) => {
  const [year, month, day] = iso.split('-').map(Number)
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay()
  return weekday === 0 || weekday === 6
}

// A day is tinted by how the schedule meets the staffing targets, and says so in
// words as well; a day with no target stays plain.
const TINT: Partial<Record<CoverageReportStatus, { cell: string; text: string; swatch: string }>> = {
  understaffed: { cell: 'border-error/50 bg-error/10', text: 'text-error', swatch: 'border-error/50 bg-error/10' },
  'missing-qualification': { cell: 'border-error/50 bg-error/10', text: 'text-error', swatch: 'border-error/50 bg-error/10' },
  'at-risk': { cell: 'border-warning/50 bg-warning/10', text: 'text-warning', swatch: 'border-warning/50 bg-warning/10' },
  'adequately-staffed': { cell: 'border-success/40 bg-success/10', text: 'text-success', swatch: 'border-success/40 bg-success/10' },
  overstaffed: { cell: 'border-info/40 bg-info/10', text: 'text-info', swatch: 'border-info/40 bg-info/10' },
}

const LEGEND: CoverageReportStatus[] = ['understaffed', 'at-risk', 'adequately-staffed', 'overstaffed']

// Who is named when a day is too full for everyone: all of them when they fit,
// otherwise one fewer than the lines, with each of leave and away kept in view
// so a day holding both says who is on which.
function namesShown(day: CalendarDay) {
  if (day.absences.length <= MAX_LINES) return day.absences
  const leave = day.absences.filter((absence) => absence.kind === 'leave')
  const away = day.absences.filter((absence) => absence.kind === 'away')
  const slots = MAX_LINES - 1
  let awayShown = away.length > 0 ? Math.min(away.length, leave.length > 0 ? 1 : slots) : 0
  const leaveShown = Math.min(leave.length, slots - awayShown)
  awayShown = Math.min(away.length, slots - leaveShown)
  return [...leave.slice(0, leaveShown), ...away.slice(0, awayShown)]
}

function DayCell({ day, status }: { day: CalendarDay; status?: CoverageReportStatus }) {
  const { format } = useTimeFormat()
  const today = day.date === todayIso()
  const tint = status ? TINT[status] : undefined
  const surface = tint ? tint.cell : isWeekend(day.date) ? 'border-line bg-surface-2' : 'border-line bg-canvas'
  const shown = namesShown(day)
  const team = day.own === undefined
  return (
    <td className={`rounded-control border p-0 align-top ${surface}`}>
      <div className="h-44 overflow-hidden p-2">
        <div className="flex items-start justify-between gap-1">
          <span
            className={`flex size-7 flex-none items-center justify-center rounded-full text-small font-extrabold ${today ? 'bg-primary text-primary-ink' : ''}`}
          >
            {dayNumber(day.date)}
            <span className="sr-only">
              , {longDate(day.date)}
              {today ? ', today' : ''}
            </span>
          </span>
          {team && day.working > 0 && (
            <span className="rounded-full bg-ink px-2 py-0.5 text-caption font-bold whitespace-nowrap text-canvas">{day.working} working</span>
          )}
        </div>
        {tint && status && <span className={`mt-1 block font-bold ${tint.text}`}>{COVERAGE_REPORT_STATUS_LABEL[status]}</span>}
        {!team ? (
          <span className="mt-1 block font-semibold">{formatScheduleLabel(day.own as string, format)}</span>
        ) : (
          <>
            {day.off > 0 && <span className={`mt-1 block text-ink-muted ${day.working === 0 ? 'font-semibold' : ''}`}>{day.off} off</span>}
            {(day.leave > 0 || day.other > 0) && (
              <p className="mt-1 flex flex-wrap gap-x-2 font-semibold">
                {day.leave > 0 && <span className="text-info">{day.leave} on leave</span>}
                {day.other > 0 && <span className="text-warning">{day.other} away</span>}
              </p>
            )}
            {day.absences.length > 0 && (
              <ul className="mt-0.5">
                {shown.map((absence) => (
                  <li
                    key={`${absence.employee}-${absence.label}`}
                    title={`${absence.employee}: ${absence.label}`}
                    className={`truncate ${absence.kind === 'leave' ? 'text-info' : 'text-warning'}`}
                  >
                    {absence.employee}: {absence.label}
                  </li>
                ))}
                {day.absences.length > shown.length && <li className="text-ink-muted">+{day.absences.length - shown.length} more</li>}
              </ul>
            )}
          </>
        )}
      </div>
    </td>
  )
}

/**
 * The pay period schedule as a calendar of weeks, the alternative to the list:
 * what the team looks like each day, or one officer's days when the report is
 * narrowed to one. It follows the report's filters. A day is tinted by how its
 * staffing meets the targets, today is marked, and weekends sit back.
 */
export default function PayPeriodCalendar({
  rows,
  dates,
  label,
  statusByDate,
}: {
  rows: PayPeriodRow[]
  dates: string[]
  label: string
  statusByDate?: Map<string, CoverageReportStatus>
}) {
  const weeks = weeksOf(buildCalendar(rows, dates))
  return (
    <div className="rounded-card border border-line bg-canvas p-2">
      {statusByDate && statusByDate.size > 0 && (
        <ul aria-label="Coverage against targets" className="flex flex-wrap gap-x-4 gap-y-1 px-1 pb-2 text-caption text-ink-muted">
          {LEGEND.map((status) => (
            <li key={status} className="flex items-center gap-1.5">
              <span aria-hidden="true" className={`size-3 rounded-sm border ${TINT[status]?.swatch}`} />
              {COVERAGE_REPORT_STATUS_LABEL[status]}
            </li>
          ))}
        </ul>
      )}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[46rem] table-fixed border-separate border-spacing-1.5 text-left text-caption">
          <caption className="sr-only">{label}</caption>
          <thead>
            <tr>
              {WEEKDAYS.map((name) => (
                <th key={name} scope="col" className="px-1 pb-0.5 text-center text-caption font-bold tracking-wide text-ink-subtle uppercase">{name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {weeks.map((week) => (
              <tr key={week[0].date}>
                {week.map((day) =>
                  'outside' in day ? (
                    <td key={day.date} aria-label={`${longDate(day.date)}, outside the pay period`} className="h-44 rounded-control bg-surface-2/40" />
                  ) : (
                    <DayCell key={day.date} day={day} status={statusByDate?.get(day.date)} />
                  ),
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
